import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import ModelTemplateCreateModal from '@/tiktok-platform/modules/templates/ModelTemplateCreateModal';
import FreightTemplateDetailModal from '@/tiktok-platform/modules/templates/FreightTemplateDetailModal';
import WarehouseTemplateDetailModal from '@/tiktok-platform/modules/templates/WarehouseTemplateDetailModal';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokPageShopScope, stackShopDimensionTabs } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { formatBeijingDateTime } from '@/tiktok-platform/utils/formatBeijingDateTime';
import { cellDisplayTitle } from '@/tiktok-platform/utils/cellDisplayTitle';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import { isAllShopsScope } from '@/utils/platformShopScope';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { fixedUi } from '@/i18n/fixedUi';

function enumText(value) {
  const raw = value == null ? '' : String(value).trim();
  if (!raw || raw === '—') return raw;
  return fixedUi(raw);
}

function syncKindForPage(kind) {
  if (kind === 'warehouse') return 'warehouses';
  if (kind === 'model') return 'models';
  return 'freightTemplates';
}

const WAREHOUSE_TYPE_TABS = [
  { id: 'sales', label: '发货仓', apiType: 'SALES_WAREHOUSE' },
  { id: 'return', label: '退货仓', apiType: 'RETURN_WAREHOUSE' },
];

function warehouseApiType(tabId) {
  const hit = WAREHOUSE_TYPE_TABS.find((t) => t.id === tabId);
  return hit?.apiType || 'SALES_WAREHOUSE';
}

function rowKeyOf(kind, row) {
  if (kind === 'warehouse') {
    return String(row?.warehouseId || row?.id || row?.rowId || '');
  }
  if (kind === 'model') {
    return String(row?.modelId || row?.id || row?.rowId || '');
  }
  const freightId = String(row?.freightTemplateId || row?.id || row?.rowId || '');
  const warehouseId = String(row?.warehouseId || '').trim();
  return warehouseId ? `${warehouseId}::${freightId}` : freightId;
}

/**
 * 运费模板 / 发货仓：无官方库与新建，仅展示店铺同步缓存，通过「同步更新」拉取平台数据。
 * 模特库：同步缓存 + 平台新建 / 编辑（bg.glo.modelinfo.add / edit）。
 *
 * @param {{
 *   kind: 'freight' | 'warehouse' | 'model',
 *   libraryLabel: string,
 *   idColumnLabel: string,
 *   nameColumnLabel: string,
 *   syncHint?: string,
 * }} props
 */
export default function ShopSyncedMaterialListPage({
  kind,
  libraryLabel,
  idColumnLabel,
  nameColumnLabel,
  syncHint: syncHintProp,
}) {
  const { t } = useTranslation('workbench');
  const syncHint = syncHintProp || t('syncedLib.defaultHintTiktok');
  const allowModelMutate = kind === 'model';
  const {
    shopDimensionTabs,
    pageShopScope,
    concreteScopeId,
    listReady,
  } = useTiktokPageShopScope();

  const shopId = listReady && concreteScopeId ? String(concreteScopeId) : '';
  const needsConcreteShop = isAllShopsScope(pageShopScope);
  const syncKind = syncKindForPage(kind);
  const syncKinds = useMemo(() => [syncKind], [syncKind]);
  const { runBlocking, isBusy } = useTiktokSyncJobs();
  const syncBusy = Boolean(shopId) && isBusy(syncKind, shopId);

  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [modelTypeDraft, setModelTypeDraft] = useState('');
  const [modelType, setModelType] = useState('');
  const [warehouseTypeTab, setWarehouseTypeTab] = useState('sales');
  const [warehouseTypeCounts, setWarehouseTypeCounts] = useState({ sales: 0, return: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [syncedAt, setSyncedAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [warehouseDetail, setWarehouseDetail] = useState(null);
  const [freightDetailRow, setFreightDetailRow] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [exporting, setExporting] = useState(false);
  const [queryNonce, setQueryNonce] = useState(0);

  const warehouseTypeLabel = warehouseTypeTab === 'return' ? t('warehouseLib.returnWh') : t('warehouseLib.salesWh');
  const activeLibraryLabel = kind === 'warehouse' ? warehouseTypeLabel : libraryLabel;

  const selectableKeys = useMemo(
    () => items.map((row) => rowKeyOf(kind, row)).filter(Boolean),
    [items, kind],
  );
  const allPageSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  /** 进页 / 查询：读本地缓存 */
  const load = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setSyncedAt('');
      setWarehouseTypeCounts({ sales: 0, return: 0 });
      return false;
    }
    setLoading(true);
    try {
      let res;
      if (kind === 'warehouse') {
        res = await tiktokPlatformAPI.getShopWarehouses(shopId, {
          keyword: keyword || undefined,
          warehouseType: warehouseApiType(warehouseTypeTab),
          page,
          pageSize,
        });
        setWarehouseTypeCounts({
          sales: Number(res?.salesCount) || 0,
          return: Number(res?.returnCount) || 0,
        });
      } else if (kind === 'model') {
        if (modelType) {
          let synced = '';
          const all = await fetchAllPages(async (pageNo, size) => {
            const r = await tiktokPlatformAPI.listShopModels(shopId, {
              keyword: keyword || undefined,
              page: pageNo,
              pageSize: size,
            });
            if (pageNo === 1) synced = r?.syncedAt || '';
            return {
              items: Array.isArray(r?.items) ? r.items : [],
              total: Number(r?.total) || 0,
            };
          });
          const filtered = all.filter((row) => String(row.modelType) === String(modelType));
          const start = (Math.max(1, page) - 1) * pageSize;
          setItems(filtered.slice(start, start + pageSize));
          setTotal(filtered.length);
          if (synced) setSyncedAt(synced);
          return true;
        }
        res = await tiktokPlatformAPI.listShopModels(shopId, {
          keyword: keyword || undefined,
          page,
          pageSize,
        });
      } else {
        res = await tiktokPlatformAPI.listShopFreightTemplates(shopId, {
          keyword: keyword || undefined,
          page,
          pageSize,
        });
      }
      const list = Array.isArray(res?.items)
        ? res.items
        : (Array.isArray(res?.warehouses) ? res.warehouses : []);
      setItems(list);
      setTotal(Number(res?.total) || list.length);
      if (res?.syncedAt) setSyncedAt(res.syncedAt);
      return true;
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('syncedLib.loadFailed', { name: activeLibraryLabel }), 'error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    shopId,
    kind,
    keyword,
    modelType,
    warehouseTypeTab,
    page,
    pageSize,
    activeLibraryLabel,
    queryNonce,
    t,
  ]);

  useEffect(() => {
    setPage(1);
    setKeyword('');
    setKeywordDraft('');
    setModelType('');
    setModelTypeDraft('');
    setWarehouseTypeTab('sales');
    setWarehouseTypeCounts({ sales: 0, return: 0 });
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    setQueryNonce(0);
  }, [shopId, kind]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [keyword, modelType, warehouseTypeTab]);

  // 进页默认拉本地缓存；查询/重置通过 queryNonce 强制刷新
  useEffect(() => {
    void load();
  }, [load]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcreteSync') : t('returnList.pickShop'), 'warning');
      return;
    }
    setPage(1);
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    try {
      const syncLabel = libraryLabel;
      const syncRes = await runBlocking({
        kind: syncKind,
        shopId,
        label: t('syncedLib.syncJob', { name: syncLabel }),
        syncFn: async () => {
          if (kind === 'warehouse') {
            return tiktokPlatformAPI.syncShopWarehouses(shopId);
          }
          if (kind === 'freight') {
            return tiktokPlatformAPI.syncShopFreightTemplates(shopId);
          }
          if (typeof tiktokPlatformAPI.syncShopModels === 'function') {
            return tiktokPlatformAPI.syncShopModels(shopId);
          }
          return {
            syncedAt: new Date().toISOString(),
            message: t('modelLib.syncPending'),
            warning: true,
            count: 0,
          };
        },
        buildSuccess: (res) => {
          const count = Number(res?.count);
          return {
            imported: Number.isFinite(count) ? count : 0,
            message: res?.message
              || (Number.isFinite(count)
                ? t('syncedLib.syncedCount', { name: syncLabel, count })
                : t('syncedLib.synced', { name: syncLabel })),
            toastType: res?.warning ? 'warning' : 'success',
            silent: true,
          };
        },
      });
      if (syncRes?.syncedAt) setSyncedAt(String(syncRes.syncedAt));
      else setSyncedAt(new Date().toISOString());
      await load();
      const count = Number(syncRes?.count);
      showToast(
        syncRes?.message
        || (Number.isFinite(count) ? t('syncedLib.syncedCount', { name: syncLabel, count }) : t('syncedLib.synced', { name: syncLabel })),
        syncRes?.warning ? 'warning' : 'success',
      );
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      try {
        await load();
      } catch (_) {
        /* ignore */
      }
      showToast(e?.message || t('syncedLib.syncFailed'), 'error');
    }
  }, [shopId, needsConcreteShop, kind, syncKind, libraryLabel, load, runBlocking, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncBusy || syncingLocal;

  const toggleOne = (row) => {
    const key = rowKeyOf(kind, row);
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedKeys((prev) => {
      const next = togglePageSelection(prev, selectableKeys, allPageSelected);
      if (allPageSelected) {
        selectableKeys.forEach((key) => selectedRowsRef.current.delete(key));
      } else {
        items.forEach((row) => {
          const key = rowKeyOf(kind, row);
          if (key) selectedRowsRef.current.set(key, row);
        });
      }
      return next;
    });
  };

  const resolveSelectedRows = () => (
    [...selectedKeys].map((key) => (
      selectedRowsRef.current.get(key)
      || items.find((row) => rowKeyOf(kind, row) === key)
    )).filter(Boolean)
  );

  const mapRowsToCsv = (rows) => {
    let headers;
    let mapped;
    if (kind === 'warehouse') {
      headers = [
        t('warehouseLib.colId'), t('warehouseLib.colName'), t('warehouseLib.colType'), t('warehouseLib.subType'), t('warehouseLib.effect'), t('warehouseLib.defaultWh'), t('warehouseLib.entityId'),
        t('warehouseLib.fullAddress'), t('warehouseLib.region'), t('warehouseLib.state'), t('warehouseLib.city'), t('warehouseLib.district'), t('warehouseLib.postal'), t('warehouseLib.contact'), t('warehouseLib.phone'), t('freightLib.colSynced'),
      ];
      mapped = rows.map((row) => ({
        [t('warehouseLib.colId')]: row.warehouseId || '',
        [t('warehouseLib.colName')]: row.name || row.warehouseName || '',
        [t('warehouseLib.colType')]: enumText(row.warehouseTypeLabel),
        [t('warehouseLib.subType')]: enumText(row.subTypeLabel || row.subType),
        [t('warehouseLib.effect')]: enumText(row.effectStatusLabel || row.effectStatus),
        [t('warehouseLib.defaultWh')]: row.isDefault === true ? t('returnDetail.yes') : (row.isDefault === false ? t('returnDetail.no') : ''),
        [t('warehouseLib.entityId')]: row.entityId || '',
        [t('warehouseLib.fullAddress')]: row.fullAddress || '',
        [t('warehouseLib.region')]: row.region || row.regionCode || '',
        [t('warehouseLib.state')]: row.state || '',
        [t('warehouseLib.city')]: row.city || '',
        [t('warehouseLib.district')]: row.district || '',
        [t('warehouseLib.postal')]: row.postalCode || '',
        [t('warehouseLib.contact')]: row.contactPerson || '',
        [t('warehouseLib.phone')]: row.phoneNumber || '',
        [t('freightLib.colSynced')]: formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '',
      }));
    } else if (kind === 'model') {
      headers = [idColumnLabel, nameColumnLabel, t('modelLib.type'), t('modelLib.height'), t('modelLib.bust'), t('modelLib.waist'), t('modelLib.hip'), t('productTemplate.colCreatedAt'), t('productTemplate.colUpdatedAt')];
      mapped = rows.map((row) => ({
        [idColumnLabel]: row.modelId || row.id || '',
        [nameColumnLabel]: row.name || row.modelName || '',
        [t('modelLib.type')]: enumText(row.modelTypeLabel),
        [t('modelLib.height')]: row.height || '',
        [t('modelLib.bust')]: row.bust || '',
        [t('modelLib.waist')]: row.waist || '',
        [t('modelLib.hip')]: row.hip || '',
        [t('productTemplate.colCreatedAt')]: formatBeijingDateTime(row.createdAt),
        [t('productTemplate.colUpdatedAt')]: formatBeijingDateTime(row.lastSyncedAt || row.updatedAt),
      }));
    } else {
      headers = [
        t('freightLib.colId'), t('freightLib.colName'), t('freightLib.colParty'), t('freightLib.colDefault'), t('freightLib.colAvailable'), t('freightLib.colUnavailableReason'), t('freightLib.colSynced'),
      ];
      mapped = rows.map((row) => ({
        [t('freightLib.colId')]: row.freightTemplateId || row.id || '',
        [t('freightLib.colName')]: row.name || row.templateName || '',
        [t('freightLib.colParty')]: enumText(row.templatePartyLabel),
        [t('freightLib.colDefault')]: row.isDefault === true ? t('returnDetail.yes') : (row.isDefault === false ? t('returnDetail.no') : ''),
        [t('freightLib.colAvailable')]: row.isAvailable === true ? t('brandLib.available') : (row.isAvailable === false ? t('brandLib.unavailable') : ''),
        [t('freightLib.colUnavailableReason')]: row.description || '',
        [t('freightLib.colSynced')]: formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '',
      }));
    }
    return { headers, mapped };
  };

  const exportCsvRows = (rows) => {
    if (!rows.length) {
      showToast(t('brandLib.nothingToExport'), 'warning');
      return;
    }
    const { headers, mapped } = mapRowsToCsv(rows);
    downloadCsv(`tiktok-${kind}-${Date.now()}.csv`, headers, mapped);
    showToast(t('tiktokCatalog.exported', { count: rows.length }), 'success');
  };

  const fetchFilteredRows = useCallback(async () => {
    if (!shopId) return [];
    const all = await fetchAllPages(async (pageNo, size) => {
      let res;
      if (kind === 'warehouse') {
        res = await tiktokPlatformAPI.getShopWarehouses(shopId, {
          keyword: keyword || undefined,
          warehouseType: warehouseApiType(warehouseTypeTab),
          page: pageNo,
          pageSize: size,
        });
      } else if (kind === 'model') {
        res = await tiktokPlatformAPI.listShopModels(shopId, {
          keyword: keyword || undefined,
          page: pageNo,
          pageSize: size,
        });
      } else {
        res = await tiktokPlatformAPI.listShopFreightTemplates(shopId, {
          keyword: keyword || undefined,
          page: pageNo,
          pageSize: size,
        });
      }
      const list = Array.isArray(res?.items)
        ? res.items
        : (Array.isArray(res?.warehouses) ? res.warehouses : []);
      return { items: list, total: Number(res?.total) || list.length };
    });
    if (kind === 'model' && modelType) {
      return all.filter((row) => String(row.modelType) === String(modelType));
    }
    return all;
  }, [shopId, kind, keyword, modelType, warehouseTypeTab]);

  const onWarehouseTypeChange = (next) => {
    if (!WAREHOUSE_TYPE_TABS.some((t) => t.id === next) || next === warehouseTypeTab) return;
    setWarehouseTypeTab(next);
    setWarehouseDetail(null);
    setPage(1);
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  };

  const warehouseTypeTabs = kind === 'warehouse' ? (
    <div className="tko-status-tabs" role="tablist" aria-label={t('warehouseLib.typeAria')}>
      {WAREHOUSE_TYPE_TABS.map((tab) => {
        const active = warehouseTypeTab === tab.id;
        const count = Number(warehouseTypeCounts[tab.id]) || 0;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tko-status-tab${active ? ' tko-status-tab--active' : ''}`}
            onClick={() => onWarehouseTypeChange(tab.id)}
          >
            {tab.id === 'return' ? t('warehouseLib.returnWh') : t('warehouseLib.salesWh')}
            <span className="tko-status-tab__count">{count}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  const listTabs = stackShopDimensionTabs(shopDimensionTabs, warehouseTypeTabs);

  const onExportCsvFiltered = async () => {
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcrete') : t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchFilteredRows();
      exportCsvRows(rows);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onExportCsvPage = () => {
    exportCsvRows(items);
  };

  const onExportCsvSelected = () => {
    const rows = resolveSelectedRows();
    if (!rows.length) {
      showToast(t('brandLib.pickExport'), 'warning');
      return;
    }
    exportCsvRows(rows);
  };

  const onDeleteLocal = async (ids, { title } = {}) => {
    const clean = (Array.isArray(ids) ? ids : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean)
      .map((id) => {
        // 运费行 key 可能是 warehouseId::deliveryOptionId
        if (kind === 'freight' && id.includes('::')) {
          return id.slice(id.lastIndexOf('::') + 2);
        }
        return id;
      })
      .filter(Boolean);
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcrete') : t('returnList.pickShop'), 'warning');
      return;
    }
    if (!clean.length) {
      showToast(t('syncedLib.pickDelete'), 'warning');
      return;
    }
    const ok = await showTiktokConfirm({
      title: title || t('syncedLib.deleteTitle', { name: libraryLabel }),
      message: t('syncedLib.deleteMsgTiktok', { count: clean.length, name: libraryLabel }),
      confirmText: t('syncedLib.deleteLocal'),
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      let deleted = clean.length;
      if (kind === 'warehouse') {
        const res = await tiktokPlatformAPI.batchDeleteShopWarehouses(shopId, clean);
        deleted = Number(res?.deleted ?? clean.length);
      } else if (kind === 'model') {
        const res = await tiktokPlatformAPI.batchDeleteShopModels(shopId, clean, { platform: false });
        deleted = Number(res?.deleted ?? clean.length);
      } else {
        const res = await tiktokPlatformAPI.batchDeleteShopFreightTemplates(shopId, clean);
        deleted = Number(res?.deleted ?? clean.length);
      }
      showToast(t('syncedLib.deletedLocal', { count: deleted }), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        clean.forEach((id) => next.delete(id));
        return next;
      });
      clean.forEach((id) => selectedRowsRef.current.delete(id));
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchDelete = async () => {
    await onDeleteLocal([...selectedKeys], { title: t('syncedLib.batchDeleteTitle', { name: libraryLabel }) });
  };

  const onDeleteOne = async (row) => {
    const key = rowKeyOf(kind, row);
    if (!key) {
      showToast(t('syncedLib.unrecognized'), 'warning');
      return;
    }
    await onDeleteLocal([key], { title: t('syncedLib.deleteTitle', { name: libraryLabel }) });
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setModelType(modelTypeDraft);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setModelTypeDraft('');
    setModelType('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const openCreate = () => {
    if (!allowModelMutate) return;
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcrete') : t('returnList.pickShop'), 'warning');
      return;
    }
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    if (!allowModelMutate || !row) return;
    if (row.canEdit === false) {
      showToast(t('modelLib.cannotEdit'), 'warning');
      return;
    }
    setEditingRow(row);
    setModalOpen(true);
  };

  const onSaveModel = async (payload) => {
    if (!allowModelMutate || !shopId) return;
    setSaving(true);
    try {
      const modelId = editingRow?.modelId || editingRow?.id;
      if (modelId) {
        await tiktokPlatformAPI.updateShopModel(shopId, modelId, payload);
        showToast(t('modelLib.updated'), 'success');
      } else {
        await tiktokPlatformAPI.createShopModel(shopId, payload);
        showToast(t('modelLib.created'), 'success');
      }
      setModalOpen(false);
      setEditingRow(null);
      await load();
    } catch (e) {
      showToast(e?.message || t('modelLib.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => {
    if (kind === 'warehouse') {
      return [
        { key: 'warehouseId', title: t('warehouseLib.colId') },
        { key: 'name', title: t('warehouseLib.colName') },
        { key: 'warehouseType', title: t('warehouseLib.colType') },
        { key: 'subType', title: t('warehouseLib.subType') },
        { key: 'effectStatus', title: t('warehouseLib.effect') },
        { key: 'isDefault', title: t('warehouseLib.defaultWh') },
        { key: 'entityId', title: t('warehouseLib.entityId') },
        { key: 'synced', title: t('freightLib.colSynced') },
        { key: 'ops', title: t('returnList.actions') },
      ];
    }
    if (kind === 'model') {
      return [
        { key: 'thumb', title: t('modelLib.thumb') },
        { key: 'name', title: nameColumnLabel },
        { key: 'modelType', title: t('modelLib.type') },
        { key: 'height', title: t('modelLib.height') },
        { key: 'bust', title: t('modelLib.bust') },
        { key: 'waist', title: t('modelLib.waist') },
        { key: 'hip', title: t('modelLib.hip') },
        { key: 'created', title: t('productTemplate.colCreatedAt') },
        { key: 'updated', title: t('productTemplate.colUpdatedAt') },
        { key: 'ops', title: t('returnList.actions') },
      ];
    }
    return [
      { key: 'freightTemplateId', title: t('freightLib.colId') },
      { key: 'name', title: nameColumnLabel },
      { key: 'templateParty', title: t('freightLib.colParty') },
      { key: 'isDefault', title: t('freightLib.colDefault') },
      { key: 'availability', title: t('freightLib.colAvailable') },
      { key: 'synced', title: t('freightLib.colSynced') },
      { key: 'ops', title: t('returnList.actions') },
    ];
  }, [kind, nameColumnLabel, t]);

  const colSpan = columns.length + 1;

  const emptyHint = needsConcreteShop
    ? t('syncedLib.pickConcreteView')
    : (!shopId
      ? t('returnList.pickShop')
      : (loading
        ? t('returnList.loading')
        : (keyword || modelType
          ? t('syncedLib.noMatch', { name: activeLibraryLabel })
          : (allowModelMutate
            ? t('modelLib.emptyCreateTiktok', { name: activeLibraryLabel })
            : t('syncedLib.emptyPullTiktok', { name: activeLibraryLabel })))));

  const lastSyncedLabel = syncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(syncedAt) })
    : t('tiktokCatalog.notSynced');

  const busy = loading || deleting || syncing || saving || exporting;

  return (
    <>
      <TiktokSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={busy}
            syncKinds={syncKinds}
            quotaKind={syncKind}
            onSync={guardedSync}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || busy}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={syncHint}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || busy}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: onExportCsvFiltered,
              onExportPage: onExportCsvPage,
              onExportSelected: onExportCsvSelected,
            })}
            batchOpsMenu={[
              {
                key: 'batch-delete',
                label: t('returnList.batchDelete'),
                tone: 'danger',
                disabled: deleting || selectedKeys.size === 0,
                onClick: onBatchDelete,
              },
            ]}
            extraActions={[
              ...(allowModelMutate ? [{
                key: 'create-model',
                label: t('modelLib.create'),
                tone: 'primary',
                disabled: !shopId || busy,
                onClick: openCreate,
              }] : []),
            ]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline">
              <span className="tko-field__label">{nameColumnLabel}</span>
              <input
                className="tko-input"
                value={keywordDraft}
                disabled={!shopId}
                placeholder={t('syncedLib.keywordPh', { name: nameColumnLabel })}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runQuery();
                }}
              />
            </div>
            {kind === 'model' ? (
              <div className="tko-field tko-field--inline">
                <span className="tko-field__label">{t('modelLib.type')}</span>
                <select
                  className="tko-select"
                  value={modelTypeDraft}
                  disabled={!shopId}
                  onChange={(e) => setModelTypeDraft(e.target.value)}
                >
                  <option value="">{t('modelLib.allTypes')}</option>
                  <option value="0">{t('modelLib.clothes')}</option>
                  <option value="1">{t('modelLib.shoeTiktok')}</option>
                </select>
              </div>
            ) : null}
          </div>
          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--ghost tko-btn--sm"
              disabled={!shopId || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!shopId || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('productTemplate.querying')}
      >
        <div className="tko-drafts__table-wrap tko-certs-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-certs-table tko-tpl-table tko-tpl-table--synced">
            <colgroup>
              <col className="tko-certs-col--check" />
              {columns.map((col) => (
                <col key={col.key} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-certs-th tko-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allPageSelected}
                    onChange={toggleAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                {columns.map((col) => (
                  <th key={col.key} scope="col" className="tko-certs-th">{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!items.length ? (
                <tr>
                  <td colSpan={colSpan} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKeyOf(kind, row);
                const checked = selectedKeys.has(key);
                const rowEditable = allowModelMutate && row.canEdit !== false;
                return (
                  <tr
                    key={key || `${row.name}-${Math.random()}`}
                    className="tko-row--clickable"
                    onClick={(e) => {
                      if (e.target.closest('button, a, .tiktok-draft-actions')) return;
                      toggleOne(row);
                    }}
                  >
                    <td
                      className="tko-certs-td--check"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOne(row);
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        checked={checked}
                        onChange={() => toggleOne(row)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    {kind === 'warehouse' ? (
                      <>
                        <td title={cellDisplayTitle(row.warehouseId)}>{row.warehouseId || '—'}</td>
                        <td title={cellDisplayTitle(row.name || row.warehouseName)}>{row.name || row.warehouseName || '—'}</td>
                        <td>{enumText(row.warehouseTypeLabel) || '—'}</td>
                        <td title={cellDisplayTitle(row.subType)}>{enumText(row.subTypeLabel || row.subType) || '—'}</td>
                        <td title={cellDisplayTitle(row.effectStatus)}>{enumText(row.effectStatusLabel || row.effectStatus) || '—'}</td>
                        <td>{row.isDefault === true ? t('returnDetail.yes') : (row.isDefault === false ? t('returnDetail.no') : '—')}</td>
                        <td title={cellDisplayTitle(row.entityId)}>{row.entityId || '—'}</td>
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.lastSyncedAt || row.updatedAt))}>
                          {formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '—'}
                        </td>
                        <td className="tko-certs-td tko-certs-td--ops">
                          <div className="tiktok-draft-actions">
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              title={t('warehouseLib.detailTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                setWarehouseDetail(row);
                              }}
                            >
                              {t('syncedLib.detail')}
                            </button>
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={busy || !shopId}
                              title={t('syncedLib.deleteCacheTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                void onDeleteOne(row);
                              }}
                            >
                              {t('returnList.delete')}
                            </button>
                          </div>
                        </td>
                      </>
                    ) : kind === 'model' ? (
                      <>
                        <td>
                          {row.headPortrait || row.thumbUrl ? (
                            <img
                              className="tko-tpl-thumb"
                              src={row.headPortrait || row.thumbUrl}
                              alt=""
                              width={40}
                              height={40}
                            />
                          ) : '—'}
                        </td>
                        <td title={cellDisplayTitle(row.name || row.modelName)}>{row.name || row.modelName || '—'}</td>
                        <td title={cellDisplayTitle(row.modelTypeLabel)}>{enumText(row.modelTypeLabel) || '—'}</td>
                        <td>{row.height || '—'}</td>
                        <td>{row.bust || '—'}</td>
                        <td>{row.waist || '—'}</td>
                        <td>{row.hip || '—'}</td>
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.createdAt))}>
                          {formatBeijingDateTime(row.createdAt)}
                        </td>
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.lastSyncedAt || row.updatedAt))}>
                          {formatBeijingDateTime(row.lastSyncedAt || row.updatedAt)}
                        </td>
                        <td className="tko-certs-td tko-certs-td--ops">
                          <div className="tiktok-draft-actions">
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={busy || !rowEditable}
                              title={rowEditable ? t('modelLib.editModel') : t('modelLib.locked')}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(row);
                              }}
                            >
                              {t('productTemplate.edit')}
                            </button>
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={busy || !shopId}
                              title={t('syncedLib.deleteCacheTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                void onDeleteOne(row);
                              }}
                            >
                              {t('returnList.delete')}
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td title={cellDisplayTitle(row.freightTemplateId)}>{row.freightTemplateId || '—'}</td>
                        <td title={cellDisplayTitle(row.name || row.templateName)}>{row.name || row.templateName || '—'}</td>
                        <td title={cellDisplayTitle(row.templatePartyLabel)}>{enumText(row.templatePartyLabel) || '—'}</td>
                        <td>{row.isDefault === true ? t('returnDetail.yes') : (row.isDefault === false ? t('returnDetail.no') : '—')}</td>
                        <td title={cellDisplayTitle(row.description)}>
                          {row.isAvailable === true ? t('brandLib.available') : (row.isAvailable === false ? (row.description ? t('freightLib.unavailableWithReason', { reason: row.description }) : t('brandLib.unavailable')) : '—')}
                        </td>
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.lastSyncedAt || row.updatedAt))}>
                          {formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '—'}
                        </td>
                        <td className="tko-certs-td tko-certs-td--ops">
                          <div className="tiktok-draft-actions">
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              title={t('freightLib.detailTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFreightDetailRow(row);
                              }}
                            >
                              {t('syncedLib.detail')}
                            </button>
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={busy || !shopId}
                              title={t('syncedLib.deleteCacheTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                void onDeleteOne(row);
                              }}
                            >
                              {t('returnList.delete')}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          disabled={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </ProductListCard>

      {allowModelMutate ? (
        <ModelTemplateCreateModal
          open={modalOpen}
          shopId={shopId}
          initial={editingRow}
          busy={saving}
          onClose={() => {
            if (saving) return;
            setModalOpen(false);
            setEditingRow(null);
          }}
          onSubmit={onSaveModel}
        />
      ) : null}

      {kind === 'freight' ? (
        <FreightTemplateDetailModal
          open={Boolean(freightDetailRow)}
          row={freightDetailRow}
          onClose={() => setFreightDetailRow(null)}
        />
      ) : null}

      {kind === 'warehouse' ? (
        <WarehouseTemplateDetailModal
          open={Boolean(warehouseDetail)}
          warehouse={warehouseDetail}
          onClose={() => setWarehouseDetail(null)}
        />
      ) : null}
    </>
  );
}
